import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { HeaderIcon, cardHeaderStyle, cardStyle } from './shared';
import { ProfileOverviewData, VibrancyProfileService } from '../vibrancyProfileService';
import { VibrancyEngagementService } from '../vibrancyEngagementService';
import { Period } from '../vibrancyDashboardService';

const SKELETON_COUNT = 6;

function engagementSub(current: number, previous: number) {
    const diff = current - previous;
    if (diff > 0) return `↑ ${diff} vs prior`;
    if (diff < 0) return `↓ ${Math.abs(diff)} vs prior`;
    return current > 0 ? 'same as prior' : 'No activity this period';
}

function engagementDelta(current: number, previous: number) {
    const diff = current - previous;
    if (diff > 0) return `↑ +${diff}`;
    if (diff < 0) return `↓ ${diff}`;
    return '= same';
}

const SkeletonCell: React.FC<{ last: boolean }> = ({ last }) => (
    <div className={`col-6 col-md-2 px-3 py-3 position-relative placeholder-glow ${!last ? 'border-end' : ''}`}
        style={{ borderColor: '#e1e8f5' }}>
        <span className="placeholder col-8 d-block mb-2" style={{ height: 10 }} />
        <span className="placeholder col-6 d-block" style={{ height: 22 }} />
    </div>
);

/** VD-05 Profile Overview hero strip — 6 metrics with colour borders + compare rows. */
const ProfileOverviewStrip: React.FC<{ period?: Period }> = ({ period }) => {
    const {
        selectedInstituteId, selectedInstituteName,
        instituteLoaded, timeframe: globalTimeframe, compareMode,
    } = useVibrancy();
    const timeframe = period ?? globalTimeframe;   // section period overrides the global one

    const [overview, setOverview] = useState<ProfileOverviewData | null>(null);
    const [loading,  setLoading]  = useState(false);
    const [activityCounts, setActivityCounts] = useState<{ current: number; previous: number } | null>(null);

    useEffect(() => {
        if (!selectedInstituteId) { setOverview(null); return; }
        setLoading(true);
        VibrancyProfileService.getProfileOverview(selectedInstituteId, selectedInstituteName, timeframe)
            .then(r  => setOverview(r.data?.data || null))
            .catch(() => setOverview(null))
            .finally(() => setLoading(false));
    }, [selectedInstituteId, selectedInstituteName, timeframe]);

    useEffect(() => {
        if (!selectedInstituteId) { setActivityCounts(null); return; }
        setActivityCounts(null);
        VibrancyEngagementService.getActivityCounts(selectedInstituteId, timeframe)
            .then(r => {
                const current = Number(r.data?.data?.current || 0);
                const previous = Number(r.data?.data?.previous || 0);
                setActivityCounts({ current, previous });
            })
            .catch(() => {});
    }, [selectedInstituteId, timeframe]);

    const metrics = overview?.metrics.map(m => {
        if (m.label !== 'Active Engagement' || !activityCounts) return m;
        const { current, previous } = activityCounts;
        return {
            ...m,
            value: String(current),
            sub: engagementSub(current, previous),
            prev: String(previous),
            delta: engagementDelta(current, previous),
        };
    });

    if (!instituteLoaded) return null;

    return (
        <div className="card mb-3" style={cardStyle}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                <div className="fw-medium d-flex align-items-center gap-2 font-14">
                    <HeaderIcon icon="📊" bg="rgba(10,31,68,.1)" color="#0a1f44" />
                    Profile Overview — {overview?.instituteName || selectedInstituteName}
                </div>
                <span className="font-13 text-secondary">
                    {loading ? '…' : `${overview?.periodLabel ?? ''} · ${overview?.totalAlumni ?? 0} alumni`}
                </span>
            </div>

            <div className="row g-0">
                {loading ? (
                    Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                        <SkeletonCell key={i} last={i === SKELETON_COUNT - 1} />
                    ))
                ) : !overview ? (
                    <div className="col-12 py-4 text-center text-muted small">
                        No profile data available for this institute.
                    </div>
                ) : (
                    metrics?.map((m, i) => (
                        <div key={i}
                            className={`col-6 col-md-2 px-3 py-3 position-relative ${i < overview.metrics.length - 1 ? 'border-end' : ''}`}
                            style={{ borderColor: '#e1e8f5' }}>
                            <div className="text-uppercase fw-medium font-12 text-secondary mb-1">{m.label}</div>
                            <div className="fw-bold" style={{ fontSize: 24, lineHeight: 1, color: '#0a1f44' }}>{m.value}</div>
                            {compareMode && (
                                <div className="mt-2 d-flex align-items-center gap-2 pt-2 border-top"
                                    style={{ borderTopStyle: 'dashed', borderTopColor: '#e1e8f5', fontSize: 10.5 }}>
                                    <span className="text-uppercase fw-bold" style={{ color: '#8fa0c0' }}>Prev:</span>
                                    <span className="fw-bold" style={{ color: '#4a5a80' }}>{m.prev}</span>
                                    <span className="fw-bold" style={{ color: '#0f9d58' }}>{m.delta}</span>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ProfileOverviewStrip;
